import type { Expense } from 'src/types/types';
import type { IInvoiceTableFilters } from 'src/types/invoice';
import type { TableHeadCellProps } from 'src/components/table';

import { useCallback } from 'react';
import { useSetState } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fIsAfter } from 'src/utils/format-time';

import { deleteExpense } from 'src/data/api';
import { INVOICE_SERVICE_OPTIONS } from 'src/_mock';
import { DashboardContent } from 'src/layouts/dashboard';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { setAllExpenses } from 'src/features/expenseSlice/expenseSlice';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import {
  useTable,
  emptyRows,
  getComparator,
  TableEmptyRows,
  TableHeadCustom,
} from 'src/components/table';

import { ExpenseTableRow } from './expense-table-row';
import { InvoiceTableToolbar } from '../../../sections/invoice/invoice-table-toolbar';

// ----------------------------------------------------------------------

const TABLE_HEAD: TableHeadCellProps[] = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'Produkt' },
  { id: 'date', label: 'Dato' },
  { id: 'prise', label: 'Pris' },
  { id: 'quantity', label: 'Antal' },
  { id: 'total', label: 'Total' },
  { id: 'note', label: 'Note' },
  { id: 'hasMoms', label: 'Moms' },
  { id: 'expenseUrl', label: 'Bilag' },
  { id: '' },
];

// ----------------------------------------------------------------------

export function ExpenseListView() {
  const table = useTable({ defaultOrderBy: 'date', defaultOrder: 'desc' });

  const expenses = useAppSelector((state) => state.expenses.expenses);
  const dispatch = useAppDispatch();

  const filters = useSetState<IInvoiceTableFilters>({
    name: '',
    note: '',
    service: [],
    status: 'all',
    startDate: null,
    endDate: null,
  });
  const { state: currentFilters } = filters;

  const dateError = fIsAfter(currentFilters.startDate, currentFilters.endDate);

  const dataFiltered = applyFilter({
    inputData: expenses,
    comparator: getComparator(table.order, table.orderBy),
    filters: currentFilters,
    dateError,
  });

  const handleDeleteRow = useCallback(
    (id: string) => {
      deleteExpense(id).then(() => {
        const deleteRow = expenses.filter((row) => row.id !== id);
        toast.success('Udgift blev slettet!');
        dispatch(setAllExpenses(deleteRow));
      });
    },
    [table, expenses]
  );

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading="Udgifter"
        action={
          <Button
            component={RouterLink}
            href={paths.admin.expense.new}
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            Ny udgift
          </Button>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <Card>
        <InvoiceTableToolbar
          filters={filters}
          dateError={dateError}
          onResetPage={table.onResetPage}
          options={{ services: INVOICE_SERVICE_OPTIONS.map((option) => option.name) }}
        />

        <Box sx={{ position: 'relative' }}>
          <Scrollbar sx={{ minHeight: 444 }}>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 800 }}>
              <TableHeadCustom
                order={table.order}
                orderBy={table.orderBy}
                headCells={TABLE_HEAD}
                rowCount={dataFiltered.length}
                numSelected={table.selected.length}
                onSort={table.onSort}
                onSelectAllRows={(checked) =>
                  table.onSelectAllRows(
                    checked,
                    dataFiltered.map((row) => row.id)
                  )
                }
              />

              <TableBody>
                {dataFiltered.map((row) => (
                  <ExpenseTableRow
                    key={row.id}
                    row={row}
                    selected={table.selected.includes(row.id)}
                    onDeleteRow={() => handleDeleteRow(row.id)}
                    editHref={paths.admin.expense.edit(row.id)}
                  />
                ))}

                <TableEmptyRows
                  height={table.dense ? 56 : 56 + 20}
                  emptyRows={emptyRows(table.page, table.rowsPerPage, dataFiltered.length)}
                />
              </TableBody>
            </Table>
          </Scrollbar>
        </Box>
      </Card>
    </DashboardContent>
  );
}

// ----------------------------------------------------------------------

type ApplyFilterProps = {
  dateError: boolean;
  inputData: Expense[];
  filters: IInvoiceTableFilters;
  comparator: (a: any, b: any) => number;
};

function applyFilter({ inputData, comparator, filters, dateError }: ApplyFilterProps) {
  const { name, note } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  inputData = stabilizedThis.map((el) => el[0]);

  if (name) {
    const normalizedName = name.toLowerCase();

    inputData = inputData.filter(({ name: _name, note: _note }) =>
      [_name, _note].some((field) => field?.toLowerCase().includes(normalizedName))
    );
  }

  if (note) {
    const normalizedNote = note.toLowerCase();

    inputData = inputData.filter(({ note: _note }) =>
      [_note].some((field) => field?.toLowerCase().includes(normalizedNote))
    );
  }

  return inputData;
}
